/**
 * Tolerant filesystem-operation helpers.
 *
 * Batch FS work in the photo gallery (bulk deletes, one-shot migrations, cache
 * cleanup) treats per-item failures as warnings, not aborts: one weird file
 * shouldn't kill the whole batch. Every site used to inline the same
 * try/catch/console.warn pattern. These helpers centralize that invariant so
 * the call sites read at their actual level of intent and the warn semantics
 * have one canonical, tested implementation.
 *
 * `safeFsOp`        — run a single op tolerantly; return true/false.
 * `safeFsForEach`   — run an op per item; return success/failure counts.
 * `safeFsForEachAccumulate` — run an op per item that contributes a numeric
 *                              accumulator (bytes freed, items counted, etc.);
 *                              return totals plus success/failure counts.
 */

/**
 * Run an async filesystem op tolerantly: catch any error, warn with `label`,
 * return whether it succeeded.
 *
 * Use when an individual failure should not abort the surrounding batch and
 * the caller doesn't need access to the error itself.
 */
export const safeFsOp = async (
  label: string,
  op: () => Promise<void>,
): Promise<boolean> => {
  try {
    await op();
    return true;
  } catch (error) {
    console.warn(label, error);
    return false;
  }
};

/**
 * Run an async op for each item, tolerating per-item failures. Each failed
 * item logs `label(item)` so warns carry per-item context. Returns counts.
 */
export const safeFsForEach = async <T>(
  items: Iterable<T>,
  label: (item: T) => string,
  op: (item: T) => Promise<void>,
): Promise<{ successes: number; failures: number }> => {
  let successes = 0;
  let failures = 0;
  for (const item of items) {
    const ok = await safeFsOp(label(item), () => op(item));
    if (ok) successes++;
    else failures++;
  }
  return { successes, failures };
};

/**
 * Like `safeFsForEach`, but the op returns a numeric contribution (e.g. bytes
 * freed by a delete, items counted, etc.). Successful contributions are summed
 * into `total`. Failed items log and don't contribute. The op may also signal
 * "stop early" by returning a tuple `[contribution, 'stop']`.
 */
export const safeFsForEachAccumulate = async <T>(
  items: Iterable<T>,
  label: (item: T) => string,
  op: (item: T) => Promise<number | readonly [number, 'stop']>,
): Promise<{ total: number; successes: number; failures: number }> => {
  let total = 0;
  let successes = 0;
  let failures = 0;
  for (const item of items) {
    try {
      const result = await op(item);
      const [contribution, signal] = Array.isArray(result) ? result : [result, undefined];
      total += contribution;
      successes++;
      if (signal === 'stop') break;
    } catch (error) {
      console.warn(label(item), error);
      failures++;
    }
  }
  return { total, successes, failures };
};
